// Simulated scene: a taped floor, a surfer's target stance, and a pinhole
// camera matching a phone's 1× lens in landscape. World coordinates are the
// board frame (cm): x across (+ right facing the nose), y toward the nose,
// z up.

import type { Vec2 } from '../core/geometry';
import { REFERENCE, boardSpan } from '../core/board';
import { toeSideSign, type Stance, type TapeLayout } from '../core/mapping';

export interface Vec3 {
  x: number;
  y: number;
  z: number;
}

export const v3 = (x: number, y: number, z: number): Vec3 => ({ x, y, z });
export const add3 = (a: Vec3, b: Vec3): Vec3 => ({ x: a.x + b.x, y: a.y + b.y, z: a.z + b.z });
export const sub3 = (a: Vec3, b: Vec3): Vec3 => ({ x: a.x - b.x, y: a.y - b.y, z: a.z - b.z });
export const mul3 = (a: Vec3, s: number): Vec3 => ({ x: a.x * s, y: a.y * s, z: a.z * s });
export const dot3 = (a: Vec3, b: Vec3) => a.x * b.x + a.y * b.y + a.z * b.z;
export const cross3 = (a: Vec3, b: Vec3): Vec3 => ({ x: a.y * b.z - a.z * b.y, y: a.z * b.x - a.x * b.z, z: a.x * b.y - a.y * b.x });
export const len3 = (a: Vec3) => Math.hypot(a.x, a.y, a.z);
export const norm3 = (a: Vec3): Vec3 => mul3(a, 1 / (len3(a) || 1));
export const lerp3 = (a: Vec3, b: Vec3, t: number): Vec3 => ({ x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t, z: a.z + (b.z - a.z) * t });

export interface SimCamera {
  pos: Vec3;
  target: Vec3;
  hfovDeg: number;
  width: number;
  height: number;
}

export interface Projected {
  /** Normalized image coordinates. */
  x: number;
  y: number;
  depth: number;
  /** Pixels per cm at this depth (for drawing). */
  scale: number;
}

export function cameraBasis(cam: SimCamera) {
  const f = norm3(sub3(cam.target, cam.pos));
  const r = norm3(cross3(f, v3(0, 0, 1)));
  const u = cross3(r, f);
  const focal = cam.width / 2 / Math.tan(((cam.hfovDeg / 2) * Math.PI) / 180);
  return { f, r, u, focal };
}

export function projector(cam: SimCamera) {
  const { f, r, u, focal } = cameraBasis(cam);
  return (p: Vec3): Projected => {
    const d = sub3(p, cam.pos);
    const zc = Math.max(1, dot3(d, f));
    const px = cam.width / 2 + (focal * dot3(d, r)) / zc;
    const py = cam.height / 2 - (focal * dot3(d, u)) / zc;
    return { x: px / cam.width, y: py / cam.height, depth: zc, scale: focal / zc };
  };
}

export interface FootPose2D {
  centre: Vec2;
  /** Heel→toe direction on the floor, radians. */
  angle: number;
}

export interface SimWorld {
  stance: Stance;
  tape: { stripLength: number; gap: number };
  /** The rider's intended stance (feet centres on the floor). */
  targets: { front: FootPose2D; rear: FootPose2D };
  camera: SimCamera;
  /** The imaginary board the tape represents (y of tail and nose, cm). */
  board: { tail: number; nose: number };
  /** Where the toes rest when lying prone (just off the tail). */
  tailY: number;
}

export { REFERENCE };

export interface WorldOptions {
  stance?: Stance;
  gap?: number;
  stripLength?: number;
  /** Camera distance from the stance centre, cm. */
  distance?: number;
  /** Degrees from square-on toward the nose (+) or tail (−). */
  azimuthDeg?: number;
  heightCm?: number;
  /** Put the camera on the heel side instead of the toe side. */
  heelSide?: boolean;
  width?: number;
  height?: number;
}

export function makeWorld(o: WorldOptions = {}): SimWorld {
  const stance = o.stance ?? 'regular';
  const s = toeSideSign(stance);
  const gap = o.gap ?? REFERENCE.stanceWidth;
  const side = o.heelSide ? -s : s;
  const az = ((o.azimuthDeg ?? 35) * Math.PI) / 180;
  const dist = o.distance ?? 290;
  const { tail, nose } = boardSpan(0);
  const centre = v3(0, gap / 2, 0);
  const deg = (d: number) => (d * Math.PI) / 180;
  // Feet point roughly toward the toe-side rail, front foot angled to the nose.
  const toward = (rotDeg: number) => Math.atan2(Math.sin(deg(rotDeg)), s * Math.cos(deg(rotDeg)));
  return {
    stance,
    tape: { stripLength: o.stripLength ?? 70, gap },
    targets: {
      // Arches over the stringer; the slight toe-side offset is natural foot asymmetry.
      front: { centre: { x: 1 * s, y: gap }, angle: toward(REFERENCE.frontFootDeg) },
      rear: { centre: { x: -1 * s, y: 0 }, angle: toward(REFERENCE.backFootDeg) },
    },
    camera: {
      pos: v3(side * dist * Math.cos(az), centre.y + 15 + dist * Math.sin(az), o.heightCm ?? 105),
      // Frame the whole movement: lying along the board and standing on it.
      target: v3(0, centre.y + 15, 60),
      hfovDeg: 64,
      width: o.width ?? 1280,
      height: o.height ?? 720,
    },
    board: { tail, nose },
    tailY: tail - 6,
  };
}

/** Image positions of the four tape ends (what the user taps). */
export function tapeImagePoints(w: SimWorld): TapeLayout {
  const P = projector(w.camera);
  const L = w.tape.stripLength / 2;
  const D = w.tape.gap;
  const img = (x: number, y: number) => {
    const p = P(v3(x, y, 0));
    return { x: p.x, y: p.y };
  };
  return {
    rear: [img(-L, 0), img(L, 0)],
    front: [img(-L, D), img(L, D)],
    stripLength: w.tape.stripLength,
    gap: D,
  };
}
